import {
    colorBaseGreyWarm50,
    colorBaseGreyWarm100,
    layoutGutterSmall,
    layoutMarginMedium,
    layoutMarginSmall,
    ProductButton,
} from '@issuu/silkscreen';
import chunkArray from 'lodash.chunk';
import type { ArticleStorySummary as ArticleStory } from 'product-document-page-shared';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { shouldShowAds } from '../../util/ads-utils';
import { AdsStoriesInside } from '../Ads';
import DocumentContext from '../DocumentPage/DocumentContext';
import { ScreenReaderH2 } from '../ScreenReaderOnly';
import { ArticleStorySummary } from './ArticleStorySummary';

const Root = styled.div`
    padding: ${layoutMarginMedium};
    width: 100%;
    border-bottom: 0.5px solid ${colorBaseGreyWarm100};
    background-color: ${colorBaseGreyWarm50};
`;

const ArticleStoryLines = styled.div`
    display: grid;
    gap: ${layoutGutterSmall};
    grid-template-columns: auto min-content;
    grid-auto-flow: column;
`;

const ColumnStory = styled.div`
    grid-column-start: 1;
`;

const ColumnAd = styled.div`
    grid-column-start: 2;
    grid-row-end: span 5;
`;

const InlineAd = styled.div`
    border-bottom: 0.5px solid ${colorBaseGreyWarm100};
    padding-block: ${layoutMarginSmall};
`;

const ReadMore = styled.div`
    text-align: center;
    margin: ${layoutMarginSmall} auto;
`;

const ArticleStories: React.FC<{ articleStorySummaries: ArticleStory[] }> = ({
    articleStorySummaries,
}) => {
    const context = useContext(DocumentContext);
    const {
        appState: {
            currentLayout: { name: layout },
            readerViewState: [readerState],
        },
    } = context;
    const isShowingAds = shouldShowAds({ context });
    if (
        isShowingAds &&
        (layout === 'wideScreen' || (layout === 'twoColumn' && readerState === 'large'))
    ) {
        return (
            <ArticleStoryLines>
                {chunkArray(articleStorySummaries, 5).map((subList, index) => [
                    subList.map(articleStorySummary => (
                        <ColumnStory key={articleStorySummary.id}>
                            <ArticleStorySummary articleStorySummary={articleStorySummary} />
                        </ColumnStory>
                    )),
                    <ColumnAd key={`ad_${index}`}>
                        <AdsStoriesInside index={index} />
                    </ColumnAd>,
                ])}
            </ArticleStoryLines>
        );
    } else {
        const firstStories = articleStorySummaries.slice(0, 2);
        const remainingStories = articleStorySummaries.slice(2);
        return (
            <>
                {firstStories.map(articleStorySummary => (
                    <ArticleStorySummary
                        articleStorySummary={articleStorySummary}
                        key={articleStorySummary.id}
                    />
                ))}
                {chunkArray(remainingStories, 5).map((subList, index) => [
                    isShowingAds ? (
                        <InlineAd key={`ad_${index}`}>
                            <AdsStoriesInside index={index} />
                        </InlineAd>
                    ) : null,
                    subList.map(articleStorySummary => (
                        <ArticleStorySummary
                            articleStorySummary={articleStorySummary}
                            key={articleStorySummary.id}
                        />
                    )),
                ])}
            </>
        );
    }
};

export const StoriesInside: React.FC = () => {
    const context = useContext(DocumentContext);
    const { articleStorySummaries, crawler } = context;
    const [showAllArticleStories, setShowAllArticleStories] = useState(
        articleStorySummaries.length < 15 || !!crawler,
    );
    const articleList = showAllArticleStories
        ? articleStorySummaries
        : articleStorySummaries.slice(0, 15);

    return (
        <Root>
            <ScreenReaderH2>Stories inside</ScreenReaderH2>
            <ArticleStories articleStorySummaries={articleList} />

            {showAllArticleStories ? null : (
                <ReadMore>
                    <ProductButton
                        onClick={() => setShowAllArticleStories(true)}
                        variant="tertiary"
                    >
                        Show all article stories
                    </ProductButton>
                </ReadMore>
            )}
        </Root>
    );
};
